import { forwardRef } from "react";
import { panelsOf } from "@/lib/geometry";
import { buildSectionDetails } from "@/lib/sections";
import { Primitives } from "@/components/Primitives";
import { SheetFrame, F } from "@/components/sheets/SheetFrame";
import { IwinSectionSheet } from "@/components/sheets/IwinSectionSheet";
import { subtypeOf } from "@/lib/iwin";
import { placeLabels, Leaders } from "@/components/sheets/Leaders";

const DSCALES = [2, 2.5, 3, 4, 5, 6, 8, 10];
const COLS = 2, ROWS = 3, X0 = 14, Y0 = 30, BW = 141, BH = 82, LABEL_W = 58, TS = 2.1;

const Detail = ({ d, i, ks }) => {
  const bx = X0 + (i % COLS) * (BW + 2), by = Y0 + Math.floor(i / COLS) * (BH + 1.5);
  const gx = bx + 4, gy = by + 9;
  const ox = gx - d.bounds.x / ks, oy = gy + (BH - 12 - d.bounds.h / ks) / 2 - d.bounds.y / ks;
  const labelX = gx + d.bounds.w / ks + 7;
  const placed = placeLabels(d.labels, ox, oy, ks, by + 9, by + BH - 2, TS);
  return (
    <g>
      <rect x={bx} y={by} width={BW} height={BH} fill="none" stroke="#000" strokeWidth={0.25} />
      <circle cx={bx + 5} cy={by + 5} r={3} fill="#0E7490" />
      <text x={bx + 5} y={by + 6.1} fontSize={3} fill="#fff" fontFamily={F} textAnchor="middle" fontWeight="bold">{String.fromCharCode(65 + i)}</text>
      <text x={bx + 10} y={by + 6.2} fontSize={3} fill="#000" fontFamily={F} fontWeight="bold">{d.title}</text>
      <text x={bx + BW - 3} y={by + 6.2} fontSize={2.4} fill="#555" fontFamily={F} textAnchor="end">{`SCALE 1:${ks}  ·  EXT | INT`}</text>
      <g transform={`translate(${ox} ${oy}) scale(${1 / ks})`}>
        <Primitives prims={d.prims} u={0.17 * ks} palette="print" font={F} />
      </g>
      <Leaders placed={placed} labelX={labelX} ts={TS} />
    </g>
  );
};

const GenericSectionSheet = forwardRef(({ item, tb, sheetNo, total }, ref) => {
  const details = buildSectionDetails(item, panelsOf(item));
  const need = Math.max(...details.map((d) => Math.max(d.bounds.w / (BW - LABEL_W - 11), d.bounds.h / (BH - 12))));
  const traced = details.reduce((a, d) => a + (d.shapes || 0), 0);
  const ks = DSCALES.find((s) => s >= need) || Math.ceil(need);
  return (
    <SheetFrame svgRef={ref} item={item} tb={tb} sheetNo={sheetNo} total={total} suffix="S" kind="TYPICAL SECTION DETAILS"
      title="SECTION DETAILS" subtitle={`TYPICAL SECTIONS · ${item.profile_system || "YKK AP"} · ${traced ? "PROFILES TRACED FROM YKK AP CATALOGUE" : "GENERIC PROFILES"} · GASKETING & ANCHORING INDICATIVE`} scaleText={`1:${ks} @ A3`} scaleValue={ks}>
      {details.map((d, i) => <Detail key={d.title} d={d} i={i} ks={ks} />)}
      <text x={16} y={284.5} fontSize={2} fontFamily={F} fill="#555">
        Approximate details for coordination only. Profile geometry, gaskets and anchor spacing to be confirmed with the YKK AP technical catalogue and structural calculations.
      </text>
    </SheetFrame>
  );
});
GenericSectionSheet.displayName = "GenericSectionSheet";

export const SectionSheet = forwardRef((props, ref) => (subtypeOf(props.item) ? <IwinSectionSheet ref={ref} {...props} /> : <GenericSectionSheet ref={ref} {...props} />));
SectionSheet.displayName = "SectionSheet";
